import { useCallback, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBriefcase, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import type { Drawing } from '@hi-level/3d-renderer';
import type { QuoteData } from '@iqd/shared-quoting/core';
import { useSettings } from '../../shared/hooks/use-settings';
import { useDomainTracking } from '../../shared/hooks/use-domain-tracking';
import { RequestAccessModal } from './request-access-modal';
import { Button } from '../../shared/components/button';
import { Card } from '../../shared/components/card';
import { ErrorModal } from '../../shared/components/error-modal';
import { Floor2DView } from './floor-2d-view';
import { Floor3DView } from './floor-3d-view';
import { ViewToggle, type RendererView } from './view-toggle';

interface BudgetResultProps {
  quoteData: QuoteData;
  drawing: unknown;
  backsheet: unknown;
  onStartOver: () => void;
}

interface BacksheetShape {
  BudgetMezzanine?: { TotalSelling?: number; PricePerSquareMetre?: number };
  BudgetFireRating?: { TotalSelling?: number; Description?: string };
  BudgetTotal?: number;
  BudgetExtraOvers?: Array<{ Description: string; Cost: number }>;
}

const gbp = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

function formatGBP(value: number | undefined | null): string {
  return typeof value === 'number' ? gbp.format(value) : '—';
}

export function BudgetResult({ quoteData, drawing, backsheet, onStartOver }: BudgetResultProps) {
  const { settings } = useSettings();
  const { isEmbeddedOnDistributorSite } = useDomainTracking();
  const [requestAccessOpen, setRequestAccessOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [view, setView] = useState<RendererView>('3d');

  const bs = backsheet as BacksheetShape | null;

  const disclaimer = useMemo(() => {
    if (!settings) return '';
    const key = isEmbeddedOnDistributorSite ? 'embeddedBudgetDisclaimer' : 'budgetDisclaimer';
    return (settings.get(key) as string) ?? '';
  }, [settings, isEmbeddedOnDistributorSite]);

  const handleRendererError = useCallback((error: unknown) => {
    if (error instanceof Error) {
      setErrorMessage(error.message);
    } else if (typeof error === 'string') {
      setErrorMessage(error);
    } else {
      setErrorMessage('The 3D renderer encountered an error.');
    }
  }, []);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-5 space-y-5">
          <div
            className="relative overflow-hidden rounded-xl bg-gradient-to-br from-hilevel-slate to-[#1f262d] text-white p-5 sm:p-6 shadow-sm"
            data-testid="budget-headline"
          >
            <div
              aria-hidden="true"
              className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-hilevel-red/30 blur-3xl"
            />
            <div className="relative">
              <div className="flex items-start justify-between gap-3">
                <p className="text-xs uppercase tracking-wider font-semibold text-white/60">
                  Total budget
                </p>
                <button
                  type="button"
                  onClick={onStartOver}
                  data-testid="budget-start-over"
                  className="inline-flex items-center gap-1.5 rounded-md border-0 bg-white/10 hover:bg-white/15 px-2.5 py-1 text-xs font-medium text-white/80 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/30 transition cursor-pointer"
                >
                  <FontAwesomeIcon icon={faRotateLeft} className="text-xs" aria-hidden="true" />
                  Start over
                </button>
              </div>
              <p
                className="mt-1 text-3xl sm:text-4xl font-bold tracking-tight tabular-nums break-words"
                data-testid="budget-total"
              >
                {formatGBP(bs?.BudgetTotal)}{' '}
                <span className="text-lg sm:text-xl font-normal">*</span>
              </p>
              <p className="mt-2 text-xs sm:text-sm text-white/70 break-words">
                {formatGBP(bs?.BudgetMezzanine?.PricePerSquareMetre)} / m² · mezzanine{' '}
                {formatGBP(bs?.BudgetMezzanine?.TotalSelling)}
                {quoteData.GeneralData.IsFireRated && (
                  <> · fire protection {formatGBP(bs?.BudgetFireRating?.TotalSelling)}</>
                )}
              </p>
            </div>
          </div>

          <Card>
            <Card.Body className="p-5 sm:p-6">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-hilevel-gray mb-4">
                Cost breakdown
              </h3>
              <ul className="space-y-2.5 text-sm text-hilevel-slate">
                <li className="flex items-baseline justify-between gap-3">
                  <span>Mezzanine</span>
                  <span className="font-semibold tabular-nums">
                    {formatGBP(bs?.BudgetMezzanine?.TotalSelling)}
                  </span>
                </li>
                {quoteData.GeneralData.IsFireRated && (
                  <li className="flex items-baseline justify-between gap-3">
                    <span>Fire Protection</span>
                    <span className="font-semibold tabular-nums">
                      {formatGBP(bs?.BudgetFireRating?.TotalSelling)}
                    </span>
                  </li>
                )}
              </ul>

              {bs?.BudgetExtraOvers && bs.BudgetExtraOvers.length > 0 && (
                <div className="mt-4 pt-4 border-t border-hilevel-border">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-hilevel-gray mb-3">
                    Extras
                  </h4>
                  <ul className="space-y-2.5 text-sm text-hilevel-slate">
                    {bs.BudgetExtraOvers.map((item, idx) => (
                      <li key={idx} className="flex items-baseline justify-between gap-3">
                        <span>{item.Description}</span>
                        <span className="font-semibold tabular-nums">{formatGBP(item.Cost)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {bs?.BudgetFireRating?.Description && quoteData.GeneralData.IsFireRated && (
                <p className="mt-4 text-xs text-hilevel-gray">{bs.BudgetFireRating.Description}</p>
              )}

              {disclaimer && (
                <p className="mt-4 pt-4 border-t border-hilevel-border text-xs text-hilevel-gray">
                  {disclaimer}
                </p>
              )}
            </Card.Body>
          </Card>
        </div>

        <div className="lg:col-span-7">
          <Card className="h-full">
            <Card.Body className="p-4 sm:p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-hilevel-gray">
                  Your floor
                </h3>
                <ViewToggle value={view} onChange={setView} />
              </div>
              {view === '3d' ? (
                <Floor3DView drawing={drawing as Drawing | null} onError={handleRendererError} />
              ) : (
                <Floor2DView drawing={drawing} />
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      {!isEmbeddedOnDistributorSite && (
        <div className="rounded-xl border border-hilevel-border bg-hilevel-light p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <span
              aria-hidden="true"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-hilevel-red/10 text-hilevel-red"
            >
              <FontAwesomeIcon icon={faBriefcase} className="text-base" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-hilevel-slate">
                Are you a trade distributor?
              </p>
              <p className="mt-0.5 text-sm text-hilevel-gray">
                Request access to the iQD distributor portal to generate quotes.
              </p>
            </div>
            <Button onClick={() => setRequestAccessOpen(true)} className="sm:shrink-0">
              Request access
            </Button>
          </div>
        </div>
      )}

      <RequestAccessModal open={requestAccessOpen} onClose={() => setRequestAccessOpen(false)} />
      <ErrorModal
        open={!!errorMessage}
        errorMessage={errorMessage ?? ''}
        onClose={() => setErrorMessage(null)}
      />
    </div>
  );
}
